/*
 * Copyright (C) 2018 Frontier Developments PLC.
 */

import {Component} from 'vue-property-decorator';
import {mixins} from 'vue-class-component';
import SiteNavigation from '@/components/SiteNavigation/index.vue';
import SiteFooter from '@/components/SiteFooter/index.vue';
import PreFooter from '@/components/PreFooter/index.vue';
import CorporateFooter from '@/components/CorporateFooter/index.vue';
import {Action, Getter} from 'vuex-class';
import WebFontLoader from 'webfontloader';
import Notifications from '@/mixins/Notifications';
import CookieConsent from '@/components/CookieConsent/index.vue';

const namespace: string = 'settingsModule';

/**
 * Application component.
 *
 * This is the main entry point component for the vue application.  The component will load global styles and router-led
 * layout (e.g. header, content, footer) that will wrap views.
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
@Component({
    components: {
        'c-site-navigation': SiteNavigation,
        'c-site-footer': SiteFooter,
        'c-corporate-footer': CorporateFooter,
        'c-pre-footer': PreFooter,
        'c-cookie-consent': CookieConsent,
    },
})
export default class App extends mixins(Notifications) {
    @Action('updateMedia', {namespace}) public updateMedia: any;
    @Getter('consented', {namespace}) public consented!: boolean;
    /**
     * Before created life-cycle event.
     */
    public beforeCreate(): void {
        WebFontLoader.load({
            google: {
                families: ['Noto Sans:400,700'],
            },
            active: (): void => {
                sessionStorage.setItem('fonts', 'true');
            },
        });
    }

    /**
     * Created life-cycle event.
     *
     * Runs media/screen identification script.
     */
    public created(): void {
        // Screen size identification
        const wnd: any = window;
        const documentElement: any = document.documentElement;

        ((m: ((mediaQuery: string) => MediaQueryList)|null|undefined, d: DOMTokenList, a: string, b: string): void => {
            if (m && d) {
                d.add('c1', 'c2');
                if (d.contains('c2')) {
                    const q: MediaQueryList[] = [m(a + 0 + b), m(a + 42.5 + b), m(a + 71.25 + b), m(a + 90 + b)];

                    /**
                     * Add media to DOM.
                     *
                     * @param {string} media
                     */
                    const add: (...media: string[]) => void = (...media: string[]): void => {
                        d.add(...media);
                        this.updateMedia(media);
                    };

                    /**
                     * Remove media from DOM.
                     *
                     * @param {string} media
                     */
                    const remove: (...media: string[]) => void = (...media: string[]): void => {
                        d.remove(...media);
                        this.updateMedia([]);
                    };

                    /**
                     * Listener event action.
                     *
                     * @param {MediaQueryList} r
                     */
                    const f: (r: any) => void = (r: any): void => {
                        let p: string = 'desktop';
                        remove('c1', 'c2', 'phone', 'tablet', p, 'widescreen', 'lte-tablet', 'gte-tablet', 'lte-' + p,
                            'gte-' + p);
                        if (q[3].matches) {
                            p = 'widescreen';
                            add(p, 'gte-tablet', 'gte-desktop');
                        } else if (q[2].matches) {
                            add(p, 'gte-tablet', 'gte-' + p, 'lte-' + p);
                        } else if (q[1].matches) {
                            p = 'tablet';
                            add(p, 'lte-' + p, 'gte-' + p, 'lte-desktop');
                        } else if (q[0].matches) {
                            p = 'phone';
                            add(p, 'lte-tablet', 'lte-desktop');
                        } else {
                            add(p, 'gte-tablet', 'gte-' + p, 'lte-' + p);
                        }
                    };

                    for (let j: number = 0; j < 4; j++) {
                        f(q[j]);
                        q[j].addListener(f);
                    }
                }
            }
        })(wnd.matchMedia, documentElement.classList, '(min-width:', 'em)');
    }
}
